/**
 * ModalWrapper — full-viewport modal overlay for master management pages.
 *
 * Renders a portal with:
 *  - Semi-transparent backdrop (click / Esc to close)
 *  - Gradient header with icon, title, subtitle, optional badge, close & maximise buttons
 *  - Scrollable content area
 *  - Thin footer bar with optional text and action buttons
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

export interface ModalWrapperProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  icon?: string;
  gradient?: string;            // e.g. "from-indigo-500 to-indigo-600"
  headerColor?: string;         // hex color e.g. "#475569" — overrides gradient when set
  badge?: string | number;
  headerActions?: React.ReactNode;
  footerText?: string;
  footerActions?: React.ReactNode;
  children: React.ReactNode;
  size?: 'md' | 'lg' | 'xl' | 'full';
}

/** Darken a hex colour by `amount` (0-255). */
function darkenHex(hex: string, amount: number): string {
  const h = hex.replace('#', '');
  const num = parseInt(h, 16);
  const R = Math.max(0, (num >> 16) - amount);
  const G = Math.max(0, ((num >> 8) & 0x00ff) - amount);
  const B = Math.max(0, (num & 0x0000ff) - amount);
  return '#' + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1);
}

const SIZE_CLASSES: Record<string, string> = {
  md:   'max-w-4xl max-h-[85vh]',
  lg:   'max-w-6xl max-h-[90vh]',
  xl:   'max-w-7xl max-h-[95vh]',
  full: '', // handled separately
};

export function ModalWrapper({
  isOpen,
  onClose,
  title,
  subtitle,
  icon = 'ri-settings-3-line',
  gradient = 'from-indigo-500 to-indigo-600',
  headerColor,
  badge,
  headerActions,
  footerText,
  footerActions,
  children,
  size = 'lg',
}: ModalWrapperProps) {
  const [winState, setWinState] = useState<'normal' | 'max'>('normal');

  // Reset window state when modal opens
  useEffect(() => {
    if (isOpen) setWinState('normal');
  }, [isOpen]);

  // Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const isMax = winState === 'max' || size === 'full';

  const content = (
    <div
      className={
        isMax
          ? 'fixed inset-0 z-[9999]'
          : 'fixed inset-0 z-[9999] flex items-center justify-center p-4'
      }
    >
      {/* Backdrop */}
      {!isMax && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Panel */}
      <div
        className={
          isMax
            ? 'bg-white dark:bg-gray-900 fixed inset-0 flex flex-col overflow-hidden'
            : `bg-white dark:bg-gray-900 rounded-xl w-full ${SIZE_CLASSES[size] || SIZE_CLASSES.lg} flex flex-col overflow-hidden shadow-2xl relative`
        }
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div
          className={`flex-shrink-0 shadow-sm ${headerColor ? '' : `bg-gradient-to-r ${gradient}`}`}
          style={headerColor ? { background: `linear-gradient(to right, ${headerColor}, ${darkenHex(headerColor, 25)})` } : undefined}
        >
          <div className="px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                <i className={`${icon} text-lg text-white`} />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-semibold text-white truncate">{title}</h2>
                  {badge !== undefined && badge !== null && (
                    <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-white/20 text-white leading-none">
                      {badge}
                    </span>
                  )}
                </div>
                {subtitle && (
                  <p className="text-xs text-white/80 truncate">{subtitle}</p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-1.5 flex-shrink-0">
              {headerActions}
              {size !== 'full' && (
                <button
                  type="button"
                  onClick={() => setWinState(winState === 'normal' ? 'max' : 'normal')}
                  className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center text-white hover:bg-white/25 transition-colors"
                  title={winState === 'normal' ? 'Full Screen' : 'Restore'}
                >
                  <i className={`text-sm ${winState === 'normal' ? 'ri-fullscreen-line' : 'ri-fullscreen-exit-line'}`} />
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center text-white hover:bg-white/25 transition-colors"
                title="Close"
              >
                <i className="ri-close-line text-base" />
              </button>
            </div>
          </div>
        </div>

        {/* ── Content ── */}
        <div className="flex-1 overflow-auto bg-gray-50 dark:bg-gray-900">
          {children}
        </div>

        {/* ── Footer ── */}
        {(footerText || footerActions) && (
          <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-6 py-2 flex items-center justify-between">
            <span className="text-xs text-gray-400 truncate">{footerText}</span>
            {footerActions && <div className="flex items-center gap-2">{footerActions}</div>}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

export default ModalWrapper;
